<template>
    <BaseModal :visible="visible" width="280px" :z-index="zIndex" :content-class="{'confirm-dialog-panel': true, 'confirm-dialog-panel--vertical': isVertical}" @close="cancel">
        <div class="confirm-dialog-body">
            <div class="confirm-dialog-title" v-if="title">{{ title }}</div>
            <div class="confirm-dialog-message" v-if="message">{{ message }}</div>
        </div>
        <div class="confirm-dialog-actions" :class="{'confirm-dialog-actions--vertical': isVertical}">
            <button v-for="(btn, i) in buttons" :key="i" class="confirm-dialog-btn" :class="btnClass(btn)" @click="onButtonClick(btn)">{{ btn.label }}</button>
        </div>
    </BaseModal>
</template>

<script>
import BaseModal from '@/components/common/base-modal/BaseModal.vue'

export default {
    name: 'ConfirmDialog',
    components: { BaseModal },
    data() {
        return {
            visible: false,
            title: '',
            message: '',
            vertical: false,
            buttons: [],
            customButtons: false,
            zIndex: 200030,
        }
    },
    created() {
        this.pendingResolve = null
        this.pendingOnConfirm = null
        this.pendingOnCancel = null
    },
    computed: {
        isVertical() {
            return this.vertical || this.buttons.length > 2
        },
    },
    methods: {
        show(options = {}) {
            this.title = options.title || ''
            this.message = options.message || ''
            this.vertical = options.vertical || false

            if (options.buttons) {
                this.buttons = options.buttons.map((btn, i) => ({ label: btn.label, type: btn.type || 'default', value: btn.value !== undefined ? btn.value : i }))
                this.customButtons = true
                this.pendingOnConfirm = options.onConfirm || null
                this.pendingOnCancel = options.onCancel || null
            } else {
                const btns = []
                if (!options.singleButton) btns.push({ label: options.cancelText || 'Vazgec', type: 'cancel', value: false })
                btns.push({ label: options.confirmText || 'Tamam', type: options.confirmType || 'danger', value: true })
                this.buttons = btns
                this.customButtons = false
                this.pendingOnConfirm = options.onConfirm || null
                this.pendingOnCancel = options.onCancel || null
            }

            this.visible = true
            return new Promise((resolve) => { this.pendingResolve = resolve })
        },
        showFromEvent(options) {
            this.show(options)
        },
        btnClass(btn) {
            return { 'confirm-dialog-btn--cancel': btn.type === 'cancel', 'confirm-dialog-btn--danger': btn.type === 'danger', 'confirm-dialog-btn--primary': btn.type === 'primary', 'confirm-dialog-btn--default': btn.type === 'default' }
        },
        onButtonClick(btn) {
            this.visible = false
            if (this.customButtons) {
                if (btn.type === 'cancel') {
                    if (this.pendingOnCancel) this.pendingOnCancel()
                } else if (this.pendingOnConfirm) {
                    this.pendingOnConfirm(btn.value)
                }
            } else {
                if (btn.value === true && this.pendingOnConfirm) this.pendingOnConfirm()
                if (btn.value === false && this.pendingOnCancel) this.pendingOnCancel()
            }
            if (this.pendingResolve) this.pendingResolve(btn.value)
            this.cleanup()
        },
        cancel() {
            const cancelBtn = this.buttons.find(b => b.type === 'cancel')
            if (cancelBtn) {
                this.onButtonClick(cancelBtn)
            } else {
                this.visible = false
                if (this.pendingResolve) this.pendingResolve(false)
                this.cleanup()
            }
        },
        cleanup() {
            this.pendingResolve = null
            this.pendingOnConfirm = null
            this.pendingOnCancel = null
        },
    },
    mounted() {
        this.eventBus.$on('showConfirmDialog', this.showFromEvent)
    },
    beforeUnmount() {
        this.eventBus.$off('showConfirmDialog', this.showFromEvent)
    },
}
</script>

<style>
.confirm-dialog-panel {
    background: #f2f2f7;
}

.confirm-dialog-body {
    padding: 20px 16px 16px;
    text-align: center;
}

.confirm-dialog-title {
    font-size: 17px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 4px;
    line-height: 1.3;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.confirm-dialog-message {
    font-size: 13px;
    font-weight: 400;
    color: #666666;
    line-height: 1.4;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.confirm-dialog-actions {
    display: flex;
    border-top: 1px solid #d1d1d6;
}

.confirm-dialog-actions--vertical {
    flex-direction: column;
}

.confirm-dialog-btn {
    flex: 1;
    padding: 11px 8px;
    border: none;
    background: none;
    font-size: 17px;
    font-weight: 400;
    cursor: pointer;
    transition: background 0.15s;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #007AFF;
}

.confirm-dialog-btn:hover {
    background: rgba(0, 0, 0, 0.04);
}

.confirm-dialog-btn:active {
    background: rgba(0, 0, 0, 0.08);
}

.confirm-dialog-btn + .confirm-dialog-btn {
    border-left: 1px solid #d1d1d6;
}

.confirm-dialog-actions--vertical .confirm-dialog-btn + .confirm-dialog-btn {
    border-left: none;
    border-top: 1px solid #d1d1d6;
}

.confirm-dialog-btn--cancel {
    font-weight: 600;
}

.confirm-dialog-btn--danger {
    color: #FF3B30;
    font-weight: 600;
}

.confirm-dialog-btn--primary {
    color: #007AFF;
    font-weight: 600;
}

.confirm-dialog-btn--default {
    color: #007AFF;
    font-weight: 400;
}

html.dark .confirm-dialog-panel {
    background: #1c1c1e;
}

html.dark .confirm-dialog-title {
    color: #f5f5f5;
}

html.dark .confirm-dialog-message {
    color: #98989f;
}

html.dark .confirm-dialog-actions {
    border-top-color: #38383a;
}

html.dark .confirm-dialog-btn {
    color: #0A84FF;
}

html.dark .confirm-dialog-btn:hover {
    background: rgba(255, 255, 255, 0.06);
}

html.dark .confirm-dialog-btn:active {
    background: rgba(255, 255, 255, 0.1);
}

html.dark .confirm-dialog-btn + .confirm-dialog-btn {
    border-left-color: #38383a;
}

html.dark .confirm-dialog-actions--vertical .confirm-dialog-btn + .confirm-dialog-btn {
    border-left-color: transparent;
    border-top-color: #38383a;
}

html.dark .confirm-dialog-btn--danger {
    color: #FF453A;
}

html.dark .confirm-dialog-btn--primary {
    color: #0A84FF;
}

html.dark .confirm-dialog-btn--default {
    color: #0A84FF;
}

html.midnight .confirm-dialog-panel {
    background: #211D19;
}

html.midnight .confirm-dialog-title {
    color: #EAE6E1;
}

html.midnight .confirm-dialog-message {
    color: #A09A93;
}

html.midnight .confirm-dialog-actions {
    border-top-color: #3D3832;
}

html.midnight .confirm-dialog-btn {
    color: #D4915C;
}

html.midnight .confirm-dialog-btn:hover {
    background: rgba(255, 255, 255, 0.06);
}

html.midnight .confirm-dialog-btn:active {
    background: rgba(255, 255, 255, 0.1);
}

html.midnight .confirm-dialog-btn + .confirm-dialog-btn {
    border-left-color: #3D3832;
}

html.midnight .confirm-dialog-actions--vertical .confirm-dialog-btn + .confirm-dialog-btn {
    border-left-color: transparent;
    border-top-color: #3D3832;
}

html.midnight .confirm-dialog-btn--danger {
    color: #FF453A;
}

html.midnight .confirm-dialog-btn--primary {
    color: #D4915C;
}

html.midnight .confirm-dialog-btn--default {
    color: #D4915C;
}

html.cream .confirm-dialog-panel {
    background: #FEFCF8;
}

html.cream .confirm-dialog-title {
    color: #2A2622;
}

html.cream .confirm-dialog-message {
    color: #847C72;
}

html.cream .confirm-dialog-actions {
    border-top-color: #E8E3DA;
}

html.cream .confirm-dialog-btn {
    color: #059669;
}

html.cream .confirm-dialog-btn + .confirm-dialog-btn {
    border-left-color: #E8E3DA;
}

html.cream .confirm-dialog-actions--vertical .confirm-dialog-btn + .confirm-dialog-btn {
    border-left-color: transparent;
    border-top-color: #E8E3DA;
}

html.cream .confirm-dialog-btn--danger {
    color: #DC2626;
}

html.cream .confirm-dialog-btn--primary {
    color: #059669;
}

</style>
